// ✅ src/screens/ChatsScreen.js (FULL)
// UPDATE:
// ✅ unread badge now shows ONLY the current user's unread count
//    - if I'm userA -> unreadCountA
//    - if I'm userB -> unreadCountB
// ✅ includes fallback if you later store unread as unreadByUid[uid]

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function timeAgo(ms) {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

/* ---------------- component ---------------- */
export default function ChatsScreen() {
  const nav = useNavigate();

  const [myUid, setMyUid] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setMyUid(u?.uid || "");
    });
    return () => unsub();
  }, []);

  // load chats + fetch profiles
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!myUid) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const qA = query(
          collection(db, "matches"),
          where("userA", "==", myUid),
          limit(50)
        );
        const qB = query(
          collection(db, "matches"),
          where("userB", "==", myUid),
          limit(50)
        );

        const [snapA, snapB] = await Promise.all([getDocs(qA), getDocs(qB)]);

        const all = [];
        snapA.forEach((d) => all.push({ id: d.id, ...d.data() }));
        snapB.forEach((d) => all.push({ id: d.id, ...d.data() }));

        const map = new Map();
        for (const m of all) map.set(m.id, m);

        // base list
        const baseList = Array.from(map.values())
          .map((m) => {
            const otherUid = m.userA === myUid ? m.userB : m.userA;

            const lastMs =
              msFromTs(m.lastMessageAt) ||
              msFromTs(m.updatedAt) ||
              msFromTs(m.createdAt) ||
              0;

            // ✅ FIX: show ONLY my unread count (not A+B)
            // - supports either unreadCountA/unreadCountB
            // - OR unreadByUid: { [uid]: number }
            const unreadByUid =
              m.unreadByUid && typeof m.unreadByUid === "object"
                ? m.unreadByUid
                : null;
            const unread =
              unreadByUid && myUid
                ? num(unreadByUid[myUid], 0)
                : m.userA === myUid
                ? num(m.unreadCountA, 0)
                : num(m.unreadCountB, 0);

            return {
              matchId: m.id,
              otherUid: safeStr(otherUid),
              otherName: "Chat",
              otherAvatar: "",
              lastText: safeStr(m.lastMessageText || ""),
              lastMs,
              unread,
            };
          })
          .sort((a, b) => (b.lastMs || 0) - (a.lastMs || 0));

        // fetch profiles in parallel
        const uniqueOtherUids = Array.from(
          new Set(baseList.map((r) => r.otherUid).filter(Boolean))
        );

        const profilePairs = await Promise.all(
          uniqueOtherUids.map(async (uid) => {
            try {
              const s = await getDoc(doc(db, "datingProfiles", uid));
              if (!s.exists()) return [uid, null];
              const d = s.data() || {};
              const name = safeStr(d.displayName) || "Chat";
              const avatar = safeStr(d.avatar) || safeStr(d.photos?.[0]) || "";
              return [uid, { name, avatar }];
            } catch {
              return [uid, null];
            }
          })
        );

        const profileMap = new Map(profilePairs);

        const finalList = baseList.map((r) => {
          const p = profileMap.get(r.otherUid);
          return {
            ...r,
            otherName: p?.name || r.otherName || "Chat",
            otherAvatar: p?.avatar || "",
          };
        });

        if (!cancelled) setRows(finalList);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [myUid]);

  return (
    <div style={S.page}>
      {/* ✅ nicer top header */}
      <div style={S.header}>
        <div style={S.headerInner}>
          <div style={S.headerTopRow}>
            <div style={S.title}>
              💬 Chats <span style={S.titleSpark}>✨</span>
            </div>
            <div style={S.pill}>📩 Messages</div>
          </div>
          <div style={S.subtitle}>
            Your matches & convos — tap one to continue 😌
          </div>
        </div>
      </div>

      <div style={S.body}>
        {loading ? (
          <div style={S.loadingBox}>
            <div style={S.spinner} />
            <div style={S.loadingText}>Loading chats… ⏳</div>
          </div>
        ) : rows.length === 0 ? (
          <div style={S.empty}>
            <div style={S.emptyEmoji}>🫶</div>
            <div style={S.emptyTitle}>No messages yet</div>
            <div style={S.emptySub}>
              When you match with someone, your chats will show here. Say “hi”
              first 👋
            </div>
          </div>
        ) : (
          <div style={S.list}>
            {rows.map((r) => (
              <button
                key={r.matchId}
                type="button"
                onClick={() => nav(`/chat/${r.matchId}`)}
                style={S.cardBtn}
                aria-label={`Open chat with ${r.otherName}`}
              >
                <div style={S.card}>
                  {/* ✅ REAL avatar now (no generic icon) */}
                  {r.otherAvatar ? (
                    <div style={S.avatarWrap}>
                      <img
                        src={r.otherAvatar}
                        alt={r.otherName}
                        style={S.avatarImg}
                        loading="lazy"
                      />
                      <div style={S.avatarRing} />
                    </div>
                  ) : (
                    <div style={S.avatarFallback}>
                      <div style={S.avatarFallbackInner}>
                        {initials(r.otherName)}
                      </div>
                    </div>
                  )}

                  <div style={S.mid}>
                    <div style={S.nameRow}>
                      <div style={S.name}>
                        {r.otherName}{" "}
                        <span style={S.nameEmoji}>
                          {r.unread > 0 ? "🔥" : "💜"}
                        </span>
                      </div>
                      <div style={S.time}>{timeAgo(r.lastMs)}</div>
                    </div>

                    <div style={S.previewRow}>
                      <div style={S.preview}>
                        {r.lastText ? r.lastText : "Say hi 👋"}
                      </div>
                    </div>
                  </div>

                  {r.unread > 0 ? (
                    <div style={S.badgeWrap}>
                      <div style={S.badge}>{r.unread}</div>
                      <div style={S.badgeLabel}>NEW</div>
                    </div>
                  ) : (
                    <div style={S.chev} aria-hidden="true">
                      ➜
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 700px at 20% 0%, rgba(236,72,153,0.14), transparent 60%), radial-gradient(900px 600px at 80% 10%, rgba(59,130,246,0.12), transparent 55%), linear-gradient(180deg, rgba(248,250,252,1), rgba(255,255,255,1))",
  },

  header: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0.55))",
    borderBottom: "1px solid rgba(2,6,23,0.07)",
  },
  headerInner: {
    padding: "14px 14px 12px",
    maxWidth: 720,
    margin: "0 auto",
  },
  headerTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: 950,
    letterSpacing: -0.4,
    color: "rgba(2,6,23,0.92)",
  },
  titleSpark: {
    display: "inline-block",
    transform: "translateY(-1px)",
  },
  pill: {
    fontSize: 12,
    fontWeight: 900,
    padding: "8px 10px",
    borderRadius: 999,
    background: "rgba(236,72,153,0.12)",
    border: "1px solid rgba(236,72,153,0.22)",
    color: "rgba(2,6,23,0.76)",
    whiteSpace: "nowrap",
  },
  subtitle: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: 800,
    opacity: 0.72,
  },

  body: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "12px 14px 14px",
  },

  loadingBox: {
    marginTop: 18,
    padding: 16,
    borderRadius: 18,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(2,6,23,0.08)",
    boxShadow: "0 16px 50px rgba(2,6,23,0.08)",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  spinner: {
    width: 18,
    height: 18,
    borderRadius: 999,
    border: "3px solid rgba(2,6,23,0.12)",
    borderTop: "3px solid rgba(236,72,153,0.65)",
    animation: "spin 0.9s linear infinite",
  },
  loadingText: {
    fontWeight: 900,
    opacity: 0.75,
  },

  empty: {
    marginTop: 24,
    padding: 18,
    borderRadius: 22,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.80), rgba(255,255,255,0.70))",
    border: "1px solid rgba(2,6,23,0.08)",
    boxShadow: "0 18px 60px rgba(2,6,23,0.08)",
  },
  emptyEmoji: {
    fontSize: 30,
    marginBottom: 6,
  },
  emptyTitle: { fontSize: 16, fontWeight: 950 },
  emptySub: { marginTop: 6, opacity: 0.75, fontWeight: 700, lineHeight: 1.35 },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    paddingBottom: 10,
  },

  cardBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    textAlign: "left",
    cursor: "pointer",
  },

  card: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    borderRadius: 20,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0.74))",
    border: "1px solid rgba(2,6,23,0.08)",
    boxShadow: "0 16px 55px rgba(2,6,23,0.10)",
    transition: "transform 120ms ease, box-shadow 120ms ease",
  },

  // ✅ Avatar styles (real image + fallback)
  avatarWrap: {
    position: "relative",
    width: 52,
    height: 52,
    flex: "0 0 auto",
  },
  avatarImg: {
    width: 52,
    height: 52,
    borderRadius: 18,
    objectFit: "cover",
    border: "1px solid rgba(2,6,23,0.08)",
    display: "block",
  },
  avatarRing: {
    position: "absolute",
    inset: -2,
    borderRadius: 20,
    border: "2px solid rgba(236,72,153,0.18)",
    pointerEvents: "none",
  },
  avatarFallback: {
    width: 52,
    height: 52,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    background:
      "radial-gradient(40px 40px at 30% 30%, rgba(236,72,153,0.18), rgba(59,130,246,0.12))",
    border: "1px solid rgba(2,6,23,0.08)",
  },
  avatarFallbackInner: {
    width: 46,
    height: 46,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    background: "rgba(255,255,255,0.70)",
    color: "rgba(2,6,23,0.82)",
    border: "1px solid rgba(2,6,23,0.06)",
  },

  mid: {
    flex: 1,
    minWidth: 0,
  },

  nameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  name: { fontWeight: 950, fontSize: 15, color: "rgba(2,6,23,0.92)" },
  nameEmoji: { marginLeft: 4, opacity: 0.95 },
  time: { opacity: 0.62, fontSize: 12, fontWeight: 900 },

  previewRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  preview: {
    marginTop: 6,
    opacity: 0.74,
    fontSize: 13,
    fontWeight: 750,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    color: "rgba(2,6,23,0.78)",
  },

  badgeWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    flex: "0 0 auto",
  },
  badge: {
    minWidth: 24,
    height: 24,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    fontWeight: 950,
    background:
      "linear-gradient(180deg, rgba(236,72,153,0.20), rgba(236,72,153,0.10))",
    border: "1px solid rgba(236,72,153,0.28)",
    padding: "0 8px",
    boxShadow: "0 10px 24px rgba(236,72,153,0.12)",
  },
  badgeLabel: {
    fontSize: 10,
    fontWeight: 950,
    letterSpacing: 0.6,
    opacity: 0.55,
  },

  chev: {
    fontSize: 16,
    opacity: 0.45,
    fontWeight: 900,
    paddingLeft: 6,
  },
};

/* ✅ tiny global keyframe without changing other files */
if (typeof document !== "undefined") {
  const id = "chats-screen-keyframes";
  if (!document.getElementById(id)) {
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      @keyframes spin { to { transform: rotate(360deg); } }
      button[aria-label] > div:hover { transform: translateY(-1px); box-shadow: 0 22px 70px rgba(2,6,23,0.12); }
      button[aria-label] > div:active { transform: translateY(0px) scale(0.995); }
    `;
    document.head.appendChild(style);
  }
}